import { useSelector } from "@ggs/store"
import type { TripTeaserProps } from "../../components/TripTeaser/TripTeaser"
import solrConfig from "../config/solr.json"
import { SolrQueryBuilder } from "../services/solr/SolrQueryBuilder"
import { SolrApi } from "../services/solrApi"
import useDataObjectTransform from "./useDataObjectTransform"
import useUserLocation from "./useUserLocation"
import { uniqWith } from "lodash"

const queryConfig = solrConfig.query
const clientConfig = solrConfig.server
const solrClient = new SolrApi(clientConfig)

type getTripsSolrDataParams = {
  destination?: string
  country?: string
  location?: string
  tripStyles?: string
  tripTypes?: string
  budget?: {
    min: number | null
    max: number | null
  }
  duration?: {
    min: number | null
    max: number | null
  }
  currency?: string
  pageIndex?: number
  pageSize?: number
  sortField?: string | null
  sortDirection?: string
  specials?: boolean
  groups?: boolean
  limit?: number
}

const priceFields = [
  "its_package_price_aus",
  "its_package_price_cad",
  "its_package_price_eur",
  "its_package_price_gbp",
  "its_package_price_nzd",
  "its_package_price_usd",
]

const useTripsSearchData: any = () => {
  const {
    tripSearchFilterAndTransform,
    tripSearchFacetLabelValueTransform,
    tripSearchFacetLocationTransform,
    tripSearchFilterAndTransformByCountry,
  } = useDataObjectTransform()
  const { mapCountryToCurrency } = useUserLocation()
  // Determine the price field based on the user's country.
  const userCountryInfo = useSelector((state) => state.settings.country)

  /**
   * Resolve currency code for current user based on country location.
   */
  const resolveUserCurrency = () => {
    const countryCode = userCountryInfo?.code || "US"
    const countryIsEurope = userCountryInfo?.isEurope || false
    const currencyCode = mapCountryToCurrency(countryCode, countryIsEurope).toLowerCase()

    return currencyCode
  }

  /**
   * Lookup the Solr price field to use for current user.
   */
  const getUserPriceField = () => {
    // Use the provided currency code or resolve the user's currency.
    const currencyCode = resolveUserCurrency()
    const currentPriceField = `its_package_price_${currencyCode}`

    return currentPriceField
  }

  /**
   * Lookup the Solr price sort field to use for current user.
   */
  const getUserPriceSortField = () => {
    const currencyCode = resolveUserCurrency()
    const currentPriceField = `its_package_price_${currencyCode}`

    return currentPriceField
  }

  interface FacetItem {
    label: string
    value: string
  }

  interface TripsSolrData {
    count: number
    totalPages: number
    results: TripTeaserProps[] | null
    destinations: FacetItem[]
    countries: FacetItem[]
    locations?: FacetItem[]
    experiences: FacetItem[]
    prices: FacetItem[]
    pricesMinMax?: { min: number; max: number }
    durations: FacetItem[]
    durationsMinMax?: { min: number; max: number }
    rawFacets?: {
      locations: any[]
      waysToTravel: any[]
      travelStyles: any[]
      durations: any[]
    }
  }

  const getTripsSolrData = async (params: getTripsSolrDataParams = {}): Promise<TripsSolrData> => {
    const {
      destination,
      country,
      location,
      duration,
      budget,
      tripStyles,
      tripTypes,
      sortField = "score",
      sortDirection = "desc",
      pageIndex = 0,
      pageSize = 9,
      specials = false,
      groups = false,
    } = params

    // Helper function to build and execute a Solr query
    const buildAndExecuteQuery = (queryParams: any) => {
      const solrQuery = new SolrQueryBuilder(queryConfig)

      // Only add groups filter if explicitly requested
      if (groups) {
        solrQuery.customq(["bs_groups:true"])
      }

      solrQuery.q(queryParams)

      solrQuery.facet({
        on: true,
        field: [
          "ss_gw_combined_destination_str",
          "ss_gw_combined_country_str",
          "ss_gw_combined_destination_country_str",
          "ss_gw_geo_pages_keyvalue_str",
          "ss_gw_ways_to_travel_keyvalue_str",
          "ss_gw_travel_style_keyvalue_str",
          "its_duration",
          ...priceFields,
        ],
        limit: 300,
        mincount: 1,
      })
        .sort({
          its_popularity: "desc",
          ...(sortField && { [sortField]: sortDirection }),
        })
        .rows(pageSize)
        .start(pageIndex)

      // Add budget price filter when budget options are provided. (0 is a valid value)
      if (budget && (budget.min || budget.min === 0) && (budget.max || budget.max === 0)) {
        const currentPriceField = getUserPriceField()
        solrQuery.rangeFilter({
          field: currentPriceField,
          start: budget.min,
          end: budget.max,
        })
      }

      // Add duration filter when duration options are provided. (0 is a valid value)
      if (duration && (duration.min || duration.min === 0) && (duration.max || duration.max === 0)) {
        solrQuery.rangeFilter({
          field: "its_duration",
          start: duration.min,
          end: duration.max,
        })
      }

      return solrClient.runQuery(solrQuery)
    }

    // Initial query parameters
    const queryParams: any = {
      index_id: "trip_index",
      ss_type: "trip",
      ...(specials && { bs_special_offer: specials }),
      ...(tripTypes && { itm_ways_to_travel_nid: `(${tripTypes?.replace(/,/g, " OR ")})` }),
      ...(tripStyles && { itm_travel_style_nid: `(${tripStyles?.replace(/,/g, " OR ")})` }),
    }

    // Support multiple destinations with OR logic
    // Extract numeric IDs from "ID:Name" format for filtering
    if (destination) {
      if (destination.includes(',')) {
        const destinations = destination.split(',').map(d => d.trim()).filter(Boolean)
        const destinationIds = destinations.map(d => d.includes(':') ? d.split(':')[0] : d)
        queryParams.ss_gw_combined_destination = `(${destinationIds.join(" OR ")})`
      } else {
        const destinationId = destination.includes(':') ? destination.split(':')[0] : destination
        queryParams.ss_gw_combined_destination = destinationId
      }
    }

    // Support multiple countries with OR logic
    // Extract numeric IDs from "ID:Name" format for filtering
    if (country) {
      if (country.includes(',')) {
        const countries = country.split(',').map(c => c.trim()).filter(Boolean)
        const countryIds = countries.map(c => c.includes(':') ? c.split(':')[0] : c)
        queryParams.ss_gw_combined_country = `(${countryIds.join(" OR ")})`
      } else {
        const countryId = country.includes(':') ? country.split(':')[0] : country
        queryParams.ss_gw_combined_country = countryId
      }
    }

    // Handle location parameter with support for multiple locations (OR logic)
    // Extract numeric IDs from "ID:Name" format for filtering
    if (location) {
      if (location.includes(',')) {
        const locations = location.split(',').map(loc => loc.trim()).filter(Boolean)
        const locationIds = locations.map(loc => loc.includes(':') ? loc.split(':')[0] : loc)
        if (locationIds.length > 0) {
          queryParams.itm_geo_pages_id = `(${locationIds.join(" OR ")})`
        }
      } else {
        const locationId = location.includes(':') ? location.split(':')[0] : location
        queryParams.itm_geo_pages_id = locationId
      }
    }

    let response = await buildAndExecuteQuery(queryParams)
    let usedFallback = false

    // Enhanced fallback logic: if location search returns no results
    const resultCount = response?.response?.numFound ?? 0
    if (resultCount === 0 && location) {

      // First fallback: if we used ID, try name-based search
      if (location.includes(':')) {
        const [locationName] = location.split(':')
        const nameBasedQueryParams: any = {
          ...queryParams,
        }
        delete nameBasedQueryParams.itm_geo_pages_id
        nameBasedQueryParams.sm_geo_pages_title = locationName

        response = await buildAndExecuteQuery(nameBasedQueryParams)
        usedFallback = true
      }

      // Second fallback: if still no results and we have country, use country filter
      const secondResultCount = response?.response?.numFound ?? 0
      if (secondResultCount === 0 && country && !destination) {
        const countryId = country.includes(':') ? country.split(':')[0] : country
        const countryQueryParams: any = {
          ...queryParams,
          ss_gw_combined_country: countryId,
        }
        // Remove location filters (both possible field types)
        delete countryQueryParams.sm_geo_pages_title
        delete countryQueryParams.itm_geo_pages_id

        response = await buildAndExecuteQuery(countryQueryParams)
        usedFallback = true
      }
    }

    const solrRecords = response?.response?.docs || null
    const solrDestinations =
      response?.facet_counts?.facet_fields?.ss_gw_combined_destination_str || null
    const solrCountries = response?.facet_counts?.facet_fields?.ss_gw_combined_country_str || null
    const solrDestinationsAndCountries =
      response?.facet_counts?.facet_fields?.ss_gw_combined_destination_country_str || null
    const solrLocations = response?.facet_counts?.facet_fields?.ss_gw_geo_pages_keyvalue_str || null
    const solrWaysToTravel =
      response?.facet_counts?.facet_fields?.ss_gw_ways_to_travel_keyvalue_str || null
    const solrTravelStyles =
      response?.facet_counts?.facet_fields?.ss_gw_travel_style_keyvalue_str || null
    const solrPrices = response?.facet_counts?.facet_fields?.[getUserPriceField()] || null
    const solrDurations = response?.facet_counts?.facet_fields?.its_duration || null
    const count: number = response?.response?.numFound ?? 0
    const totalPages: number = Math.ceil(count / pageSize)

    const prices = tripSearchFacetLabelValueTransform(solrPrices)
    const durations = tripSearchFacetLabelValueTransform(solrDurations)

    return {
      count,
      totalPages,
      results: tripSearchFilterAndTransform(solrRecords) || null,
      destinations: tripSearchFacetLabelValueTransform(solrDestinations) || [],
      countries: tripSearchFacetLabelValueTransform(solrCountries) || [],
      locations: uniqWith([
        ...(tripSearchFacetLocationTransform(solrDestinationsAndCountries) || []),
        ...(tripSearchFacetLabelValueTransform(solrLocations) || []),
      ], (a, b) => a.label === b.label && a.value === b.value),
      experiences: [
        ...(tripSearchFacetLabelValueTransform(solrWaysToTravel, "Trip Types") || []),
        ...(tripSearchFacetLabelValueTransform(solrTravelStyles, "Trip Styles") || []),
      ],
      prices,
      pricesMinMax: {
        min: prices.length > 0 ? Math.min(...prices.map((price) => Number(price.value))) : 0,
        max: prices.length > 0 ? Math.max(...prices.map((price) => Number(price.value))) : 999999,
      },
      durations,
      durationsMinMax: {
        min: durations.length > 0 ? Math.min(...durations.map((duration) => Number(duration.value))) : 1,
        max: durations.length > 0 ? Math.max(...durations.map((duration) => Number(duration.value))) : 999,
      },
      // Include raw facet data with counts
      rawFacets: {
        locations: solrLocations || [],
        waysToTravel: solrWaysToTravel || [],
        travelStyles: solrTravelStyles || [],
        durations: solrDurations || [],
      },
    }
  }

  const prepareTripsSearchResponseData = (response: any, pageSize: number) => {
    const currentPriceField = getUserPriceField()
    const solrRecords = response?.response?.docs || null
    const solrDestinations = response?.facet_counts?.facet_fields?.ss_gw_combined_destination_str || null
    const solrLocations = response?.facet_counts?.facet_fields?.ss_gw_geo_pages_keyvalue_str || null
    const solrWaysToTravel =
      response?.facet_counts?.facet_fields?.ss_gw_ways_to_travel_keyvalue_str || null
    const solrTravelStyles =
      response?.facet_counts?.facet_fields?.ss_gw_travel_style_keyvalue_str || null
    const solrPrices = response?.facet_counts?.facet_fields?.[currentPriceField] || null
    const solrDurations = response?.facet_counts?.facet_fields?.its_duration || null
    const solrCountries = response?.facet_counts?.facet_fields?.ss_gw_combined_country_str || null
    const count: number = response?.response?.numFound ?? 0
    const totalPages: number = Math.ceil(count / pageSize)

    return {
      count,
      totalPages,
      results: tripSearchFilterAndTransformByCountry(solrRecords),
      destinations: tripSearchFacetLabelValueTransform(solrDestinations) || [],
      locations: tripSearchFacetLabelValueTransform(solrLocations) || [],
      experiences: [
        ...(tripSearchFacetLabelValueTransform(solrWaysToTravel, "Trip Types") || []),
        ...(tripSearchFacetLabelValueTransform(solrTravelStyles, "Trip Styles") || []),
      ],
      prices: tripSearchFacetLabelValueTransform(solrPrices) || [],
      durations: tripSearchFacetLabelValueTransform(solrDurations) || [],
      countries: tripSearchFacetLabelValueTransform(solrCountries) || [],
      // Include raw facet data with counts
      rawFacets: {
        locations: solrLocations || [],
        waysToTravel: solrWaysToTravel || [],
        travelStyles: solrTravelStyles || [],
        durations: solrDurations || [],
      },
    }
  }

  const getTripsSolrDataByFilters = async ({
    destination,
    country,
    location,
    tripStyles,
    tripTypes,
    budget = {
      min: null,
      max: null,
    },
    duration = {
      min: null,
      max: null,
    },
    pageIndex = 0,
    pageSize = 9,
    sortField = null,
    sortDirection = "asc",
    specials = false,
    groups = false,
  }: getTripsSolrDataParams = {}) => {
    const currentPriceField = getUserPriceField()
    const solrQuery = new SolrQueryBuilder(queryConfig)

    const queryParams: any = {
      index_id: "trip_index",
      ss_type: "trip",
      ...(specials && { bs_special_offer: specials }),
      ...(tripTypes && { itm_ways_to_travel_nid: `(${tripTypes?.replace(/,/g, " OR ")})` }),
      ...(tripStyles && { itm_travel_style_nid: `(${tripStyles?.replace(/,/g, " OR ")})` }),
    }

    // Handle destination parameter with support for multiple destinations (OR logic)
    // Extract numeric IDs from "ID:Name" format for filtering
    if (destination) {
      if (destination.includes(',')) {
        const destinations = destination.split(',').map(d => d.trim()).filter(Boolean)
        const destinationIds = destinations.map(d => d.includes(':') ? d.split(':')[0] : d)
        if (destinationIds.length > 0) {
          queryParams.itm_geo_pages_destinations_nid = `(${destinationIds.join(" OR ")})`
        }
      } else {
        const destinationId = destination.includes(':') ? destination.split(':')[0] : destination
        queryParams.itm_geo_pages_destinations_nid = destinationId
      }
    }

    // Support multiple countries with OR logic
    // Extract numeric IDs from "ID:Name" format for filtering
    if (country) {
      if (country.includes(',')) {
        const countries = country.split(',').map(c => c.trim()).filter(Boolean)
        const countryIds = countries.map(c => c.includes(':') ? c.split(':')[0] : c)
        queryParams.ss_gw_combined_country = `(${countryIds.join(" OR ")})`
      } else {
        const countryId = country.includes(':') ? country.split(':')[0] : country
        queryParams.ss_gw_combined_country = countryId
      }
    }

    // Handle location parameter with support for multiple locations (OR logic)
    // Extract numeric IDs from "ID:Name" format for filtering
    if (location) {
      if (location.includes(',')) {
        const locations = location.split(',').map(loc => loc.trim()).filter(Boolean)
        const locationIds = locations.map(loc => loc.includes(':') ? loc.split(':')[0] : loc)
        if (locationIds.length > 0) {
          queryParams.itm_geo_pages_id = `(${locationIds.join(" OR ")})`
        }
      } else {
        const locationId = location.includes(':') ? location.split(':')[0] : location
        queryParams.itm_geo_pages_id = locationId
      }
    }

    // Only add groups filter if explicitly requested
    if (groups) {
      solrQuery.customq(["bs_groups:true"])
    }

    solrQuery.q(queryParams)

    solrQuery.facet({
      on: true,
      field: [
        "ss_gw_combined_destination_str",
        "ss_gw_geo_pages_keyvalue_str",
        "ss_gw_ways_to_travel_keyvalue_str",
        "ss_gw_travel_style_keyvalue_str",
        "its_duration",
        "ss_gw_combined_country_str",
        ...priceFields,
      ],
      limit: 200,
      mincount: 1,
    })
      .sort({
        its_popularity: "desc",
        ...(sortField && { [sortField]: sortDirection }),
      })
      .rows(pageSize)
      .start(pageIndex)

    // Add budget price filter when budget options are provided.
    if (budget?.min && budget?.max) {
      solrQuery.rangeFilter({
        field: currentPriceField,
        start: budget.min,
        end: budget.max,
      })
    }

    // Add duration filter when duration options are provided.
    if (duration?.min && duration?.max) {
      solrQuery.rangeFilter({
        field: "its_duration",
        start: duration.min,
        end: duration.max,
      })
    }

    const response = await solrClient.runQuery(solrQuery)
    return prepareTripsSearchResponseData(response, pageSize)
  }

  return {
    getTripsSolrData,
    getTripsSolrDataByFilters,
    getUserPriceField,
    getUserPriceSortField,
  }
}
export default useTripsSearchData

